import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock3,
  Eye,
  Loader2,
  RefreshCw,
  UserRoundCheck,
  Users,
} from 'lucide-react';
import apiClient from '../../services/api';

interface AnalyticsSummary {
  periodDays: number;
  abandonmentAfterMinutes: number;
  totals: {
    pageViews: number;
    uniqueVisitors: number;
    formStarts: number;
    completed: number;
    abandoned: number;
    abandonedWithErrors: number;
    active: number;
    conversionRate: number;
    abandonmentRate: number;
  };
  steps: Array<{
    step: number;
    label: string;
    sessions: number;
    dropoffs: number;
    progressionRate: number;
  }>;
  errors: Array<{
    code: string;
    section: string;
    step: number | null;
    count: number;
    affectedSessions: number;
    lastOccurredAt: string;
  }>;
  daily: Array<{
    date: string;
    pageViews: number;
    formStarts: number;
    completed: number;
  }>;
}

const number = new Intl.NumberFormat('pt-BR');
const eventLabels: Record<string, string> = {
  validation_failed: 'Campos obrigatórios ou inválidos',
  invalid_cep: 'CEP inválido',
  cep_not_found: 'CEP não encontrado',
  cep_lookup_failed: 'Falha na consulta de CEP',
  missing_required_documents: 'Documentos obrigatórios ausentes',
  invalid_file_type: 'Tipo de arquivo não aceito',
  file_too_large: 'Arquivo acima do limite',
  registration_validation_failed: 'Dados anteriores incompletos',
  upload_failed: 'Falha no envio de documento',
};

const FiliationAnalytics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiClient.get<AnalyticsSummary>(`/filiation-analytics/summary?days=${days}`));
    } catch {
      setError('Não foi possível carregar o funil de filiação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  const maxDaily = useMemo(
    () => Math.max(1, ...(data?.daily.map((item) => item.pageViews) || [1])),
    [data],
  );

  if (loading && !data) {
    return <div className="min-h-[360px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cosmos-500" /></div>;
  }

  if (error && !data) {
    return (
      <div className="bg-white rounded-2xl border border-red-100 p-8 text-center">
        <AlertTriangle className="w-9 h-9 text-red-500 mx-auto mb-3" />
        <p className="text-gray-700">{error}</p>
        <button onClick={() => void load()} className="mt-4 px-4 py-2 rounded-xl bg-cosmos-900 text-white">Tentar novamente</button>
      </div>
    );
  }

  if (!data) return null;

  const cards = [
    { label: 'Acessos', value: data.totals.pageViews, note: `${number.format(data.totals.uniqueVisitors)} visitantes únicos`, icon: Eye, color: 'text-blue-600 bg-blue-50' },
    { label: 'Iniciaram', value: data.totals.formStarts, note: 'Interagiram com o cadastro', icon: Users, color: 'text-violet-600 bg-violet-50' },
    { label: 'Concluíram', value: data.totals.completed, note: `${data.totals.conversionRate}% de conversão`, icon: UserRoundCheck, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Abandonaram', value: data.totals.abandoned, note: `${data.totals.abandonmentRate}% dos acessos`, icon: Clock3, color: 'text-amber-600 bg-amber-50' },
    { label: 'Após algum erro', value: data.totals.abandonedWithErrors, note: 'Abandonos com erro registrado', icon: AlertTriangle, color: 'text-rose-600 bg-rose-50' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-cosmos-900 flex items-center gap-2"><BarChart3 className="w-6 h-6" /> Funil de Filiação</h1>
          <p className="text-sm text-gray-500 mt-1">Acompanhamento anônimo da página pública e das três etapas do cadastro.</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((period) => (
            <button key={period} onClick={() => setDays(period)} className={`px-3 py-2 rounded-xl text-sm font-semibold border ${days === period ? 'bg-cosmos-900 text-white border-cosmos-900' : 'bg-white text-gray-600 border-gray-200'}`}>
              {period} dias
            </button>
          ))}
          <button title="Atualizar" onClick={() => void load()} className="p-2.5 rounded-xl border border-gray-200 bg-white text-gray-600">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {cards.map(({ label, value, note, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
            <p className="text-3xl font-bold text-cosmos-900 mt-4">{number.format(value)}</p>
            <p className="font-semibold text-gray-700 mt-1">{label}</p>
            <p className="text-xs text-gray-400 mt-1">{note}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className="xl:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h2 className="font-bold text-cosmos-900">Conversão por etapa</h2>
          <p className="text-xs text-gray-500 mt-1">“Pararam aqui” considera apenas sessões já classificadas como abandono.</p>
          <div className="mt-6 space-y-5">
            {data.steps.map((step) => {
              const width = data.totals.pageViews > 0 ? Math.max(3, (step.sessions / data.totals.pageViews) * 100) : 0;
              return (
                <div key={step.step}>
                  <div className="flex items-center justify-between gap-4 text-sm mb-2">
                    <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-cosmos-50 flex items-center justify-center font-bold text-cosmos-700">{step.step === 4 ? <CheckCircle2 className="w-4 h-4" /> : step.step}</span><span className="font-semibold text-gray-700">{step.label}</span></div>
                    <div className="text-right"><span className="font-bold text-cosmos-900">{number.format(step.sessions)}</span>{step.dropoffs > 0 && <span className="text-xs text-rose-500 ml-2">{step.dropoffs} pararam aqui</span>}</div>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-gold-500 to-amber-400" style={{ width: `${width}%` }} /></div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h2 className="font-bold text-cosmos-900">Acessos recentes</h2>
          <p className="text-xs text-gray-500 mt-1">Últimos {Math.min(days, 14)} dias exibidos.</p>
          <div className="h-56 flex items-end gap-1.5 mt-5">
            {data.daily.slice(-14).map((item) => (
              <div key={item.date} className="flex-1 h-full flex flex-col justify-end group relative">
                <div className="absolute hidden group-hover:block bottom-full mb-2 left-1/2 -translate-x-1/2 z-10 bg-cosmos-900 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap">{item.date.split('-').reverse().join('/')}: {item.pageViews} acessos, {item.completed} conclusões</div>
                <div className="bg-blue-400 rounded-t-md min-h-[2px]" style={{ height: `${(item.pageViews / maxDaily) * 100}%` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-2"><span>{data.daily.slice(-14)[0]?.date.split('-').slice(1).reverse().join('/')}</span><span>Hoje</span></div>
        </section>
      </div>

      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="font-bold text-cosmos-900">Erros encontrados no cadastro</h2>
          <p className="text-xs text-gray-500 mt-1">Nenhum dado pessoal ou conteúdo digitado é registrado nesta análise.</p>
        </div>
        {data.errors.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Nenhum erro registrado no período.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500"><tr><th className="text-left px-6 py-3">Erro</th><th className="text-left px-4 py-3">Seção</th><th className="text-right px-4 py-3">Ocorrências</th><th className="text-right px-4 py-3">Pessoas afetadas</th><th className="text-right px-6 py-3">Última ocorrência</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.errors.map((item, index) => (
                  <tr key={`${item.code}-${item.section}-${item.step}-${index}`}>
                    <td className="px-6 py-4 font-medium text-gray-800">{eventLabels[item.code] || item.code.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-4 text-gray-600">{item.section}{item.step ? ` · etapa ${item.step}` : ''}</td>
                    <td className="px-4 py-4 text-right font-semibold">{number.format(item.count)}</td>
                    <td className="px-4 py-4 text-right">{number.format(item.affectedSessions)}</td>
                    <td className="px-6 py-4 text-right text-gray-500">{new Date(item.lastOccurredAt).toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs text-gray-400">Uma sessão é classificada como abandono após {data.abandonmentAfterMinutes} minutos sem atividade e sem concluir o envio dos documentos. Sessões em andamento: {number.format(data.totals.active)}.</p>
    </div>
  );
};

export default FiliationAnalytics;
